import { useCallback, useEffect, useRef, useState } from "react";
import type {
  SearchableRecord,
  SearchTokenize,
  WorkerRequest,
  WorkerResponse,
} from "./types";

export type { SearchableRecord, SearchTokenize };

export interface UseWorkerSearchResult {
  search: (query: string) => void;
  matchedIds: Set<string> | null;
}

/**
 * Runs a FlexSearch full-text index inside a dedicated Web Worker, so searching a
 * large in-memory list never blocks the main thread. Data-shape-agnostic: callers
 * map their own domain objects into `{ id, text }` records before passing them in,
 * so this hook can be reused for any large client-side searchable list.
 *
 * Pass `null` (e.g. a feature that only sometimes needs search) to skip spinning up a
 * worker at all - `search` becomes a no-op and `matchedIds` stays `null`.
 *
 * `tokenize` controls partial-match behavior (see {@link SearchTokenize}) - defaults to "full"
 * (any substring) to match this hook's original behavior; pass "forward" for label/word search.
 */
export function useWorkerSearch(
  records: SearchableRecord[] | null,
  tokenize?: SearchTokenize,
): UseWorkerSearchResult {
  const enabled = records !== null;
  const workerRef = useRef<Worker | null>(null);
  const requestIdRef = useRef(0);
  const [matchedIds, setMatchedIds] = useState<Set<string> | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const worker = new Worker(new URL("./search.worker.ts", import.meta.url), {
      type: "module",
    });
    workerRef.current = worker;

    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const message = event.data;
      if (message.payload.requestId === requestIdRef.current) {
        setMatchedIds(new Set(message.payload.matchedIds));
      }
    };

    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const message: WorkerRequest = { type: "INIT", payload: { records, tokenize } };
    workerRef.current?.postMessage(message);
  }, [records, tokenize, enabled]);

  const search = useCallback(
    (query: string) => {
      if (!enabled) return;
      requestIdRef.current += 1;
      if (!query.trim()) {
        setMatchedIds(null);
        return;
      }
      const message: WorkerRequest = {
        type: "SEARCH",
        payload: { query, requestId: requestIdRef.current },
      };
      workerRef.current?.postMessage(message);
    },
    [enabled],
  );

  return { search, matchedIds };
}
